import React, { useState } from "react";
import { ChevronDown, ChevronUp, Heart, Users, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    id: "bring-it-up",
    icon: Heart,
    title: "How to Bring It Up",
    color: "#C86B55",
    content: [
      {
        heading: "Choose the right moment",
        text: "Find a private, calm moment. Don't bring it up during an argument or in front of others. Let them know you're coming from a place of care, not judgment."
      },
      {
        heading: "Use 'I' statements",
        text: "Instead of 'You should leave' or 'He's terrible', try: 'I've noticed you seem unhappy. I care about you and I'm here if you want to talk.' This keeps the focus on your concern, not their choices."
      },
      {
        heading: "Be patient",
        text: "They might not be ready to talk. They might deny it. They might get defensive. That's okay. Planting the seed matters. Let them know your door is always open."
      },
      {
        heading: "Don't give ultimatums",
        text: "Saying 'Leave or I'm done' can push them further away. Instead, be a consistent, safe presence. They need to know someone believes them."
      }
    ]
  },
  {
    id: "what-not-to-say",
    icon: ShieldCheck,
    title: "What Not to Say",
    color: "#9A4D3B",
    content: [
      {
        heading: "Avoid these phrases",
        text: "'Why don't you just leave?' — Leaving is the most dangerous time. It's never 'just' leaving.\n\n'I would never let that happen to me' — This implies it's their fault. It's not.\n\n'They seem so nice though' — Abusers often are charming in public. This dismisses their experience.\n\n'Think about the children' — They are thinking about the children. This adds guilt to an already impossible situation.\n\n'You're being dramatic' — Never. Minimising their pain can be devastating."
      },
      {
        heading: "What to say instead",
        text: "'I believe you.'\n'This is not your fault.'\n'I'm here for you, no matter what you decide.'\n'You deserve to be safe.'\n'What do you need from me right now?'"
      }
    ]
  },
  {
    id: "help-without-pushing",
    icon: Users,
    title: "How to Help Without Pushing",
    color: "#4A6B53",
    content: [
      {
        heading: "Be a safe harbour",
        text: "Let them know they can come to you without judgment. Don't make them feel like a burden. Consistency and patience are your most powerful tools."
      },
      {
        heading: "Educate yourself",
        text: "Learn about the dynamics of abuse. Understanding why someone stays — trauma bonding, fear, financial dependence, children — helps you be more compassionate."
      },
      {
        heading: "Offer practical help",
        text: "If they're ready: help them research options, offer a safe place to store important documents, be a point of contact for support services. Small, concrete actions matter."
      },
      {
        heading: "Respect their timeline",
        text: "On average, someone in an abusive relationship attempts to leave 7 times before they leave for good. Each attempt is brave. Support every step, even the ones that go backwards."
      }
    ]
  },
  {
    id: "survivor-stories",
    icon: Heart,
    title: "Survivor Stories (Anonymous)",
    color: "#D2A679",
    content: [
      {
        heading: "Priya's story",
        text: "\"For years, I thought it was normal. He never hit me, so I didn't think it counted. But the constant criticism, the isolation, checking my phone — it was suffocating. My sister was the first person who said 'this isn't okay' without telling me what to do. She just kept showing up. That's what saved me.\""
      },
      {
        heading: "Meera's story",
        text: "\"I left three times before I stayed gone. Each time I went back, I felt like a failure. But each time, I also learned something. The fourth time, I was ready. If you're supporting someone who goes back — please don't give up on them.\""
      },
      {
        heading: "Ananya's story",
        text: "\"The hardest part wasn't leaving. It was believing I deserved better. Years of being told I was nothing had become my inner voice. Healing started when I began to hear other voices — kinder ones — including my own.\""
      }
    ]
  },
  {
    id: "self-care-supporters",
    icon: ShieldCheck,
    title: "Self-Care for Supporters",
    color: "#E5B95F",
    content: [
      {
        heading: "You matter too",
        text: "Supporting someone through abuse is emotionally heavy. You might feel frustrated, helpless, angry, or drained. These feelings are valid. You cannot pour from an empty cup."
      },
      {
        heading: "Set boundaries",
        text: "It's okay to say 'I care about you and I need to take care of myself too.' Being a supporter doesn't mean being available 24/7 or sacrificing your own wellbeing."
      },
      {
        heading: "Seek your own support",
        text: "Talk to a counsellor, a trusted friend, or a support group for people who are supporting survivors. You don't have to carry this alone either."
      },
      {
        heading: "Remember",
        text: "You are already helping by being here, by learning, by caring enough to read this. That matters more than you know."
      }
    ]
  },
];

function SectionCard({ section }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = section.icon;

  return (
    <div
      className="bg-white rounded-[2rem] shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 overflow-hidden transition-all duration-500"
      data-testid={`support-${section.id}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-8 flex items-start gap-4"
        data-testid={`support-toggle-${section.id}`}
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1"
          style={{ backgroundColor: section.color + "15" }}
        >
          <Icon size={20} style={{ color: section.color }} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-xl text-earth-800">{section.title}</h3>
        </div>
        <div className="flex-shrink-0 mt-1 text-earth-400">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {expanded && (
        <div className="px-8 pb-8 space-y-6 animate-fade-in-up">
          {section.content.map((item, i) => (
            <div key={i} className="pl-14">
              <h4 className="font-heading text-lg text-earth-800 mb-2">{item.heading}</h4>
              <p className="text-earth-600 text-sm leading-relaxed whitespace-pre-wrap">{item.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="support-page">
      <div className="text-center mb-12">
        <p className="text-terracotta text-sm uppercase tracking-[0.15em] font-bold mb-3">For Someone I Love</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">
          You care. That matters.
        </h1>
        <p className="text-earth-600 text-base max-w-lg mx-auto">
          If someone you love is going through something difficult, 
          you're already doing something brave by being here.
        </p>
      </div>

      <div className="space-y-4">
        {SECTIONS.map((section) => (
          <SectionCard key={section.id} section={section} />
        ))}
      </div>

      <div className="mt-12 bg-sage-light rounded-[2rem] p-8 text-center">
        <p className="text-sage font-heading text-lg mb-2">You're a good person for being here</p>
        <p className="text-earth-600 text-sm">
          The fact that you're reading this means someone is lucky to have you in their life.
          Take care of yourself too.
        </p>
      </div>
    </div>
  );
}
